import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-context";
import { ContactCentreListProps } from "./contact-centre-list.props";
import { ContactCentreListViewModel } from "./contact-centre-list.view-model";
import LoadingPlaceHolder from "src/components/molecules/loading/loading-placeholder";
import EntityList from "../entity-list/entity-list";
import { IContactCentreModel } from "src/types/models/apiModels";
import { BusinessViewModel } from "../business-list/business-list.view-model";
import LoadingModal from "src/components/molecules/loading/LoadingModal";

const ContactCentreList: React.FC<ContactCentreListProps> = ({
	onAddNewClick,
	onSelect,
	selectMode,
}) => {
	const { user } = useAuth(); // Extract user from useAuth hook
	const [data, setData] = useState<IContactCentreModel[]>([]);
	const [loading, setLoading] = useState(true);
	const [canCreateContactCenter, setCanCreateContactCenter] = useState(false);
	const [selectedContactCentre, setSelectedContactCentre] = useState<
		string | null
	>(null);

	const viewModel = useRef<ContactCentreListViewModel | null>(null);
	const businessListViewModel = useRef<BusinessViewModel | null>(null);

	const handleSelectContactCentre = (contactCentre: IContactCentreModel) => {
		setSelectedContactCentre(contactCentre.contactCentreId);
		if (onSelect) {
			onSelect(contactCentre);
		}
	};

	useEffect(() => {
		const canAddNewBrandingApplication = async () => {
			let hasApprovedBusiness = false;

			if (user && user.accessToken) {
				businessListViewModel.current = new BusinessViewModel(
					user.accessToken
				);

				const businesses =
					await businessListViewModel.current.fetchListAsync();

				hasApprovedBusiness =
					businesses.filter(
						(p) => p.status === "Active" || p.status === "Approved"
					).length > 0;
			}

			const userPermissions = user?.permissionBag.permissions;

			if (
				hasApprovedBusiness &&
				userPermissions &&
				userPermissions.filter(
					(p) => p.name === "ContactCenter" && p.value === "Create"
				).length > 0
			) {
				setCanCreateContactCenter(true);
			} else {
				setCanCreateContactCenter(false);
			}
		};

		canAddNewBrandingApplication();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			setLoading((prevValue) => {
				if (!prevValue) {
					return true;
				}
				return prevValue;
			});
			try {
				if (user && user.accessToken) {
					viewModel.current = new ContactCentreListViewModel(
						user.accessToken
					);
					const response = await viewModel.current?.fetchList();
					setData(() => response);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading((prevValue) => {
					if (prevValue) {
						return false;
					}
					return prevValue;
				});
			}
		};

		fetchData();
	}, [user]);

	const renderContactCentreItem = (cc: IContactCentreModel) => (
		<>
			<div className="flex flex-col items-start justify-center gap-2 w-full p-2">
				<div className="flex items-center justify-center gap-2">
					<span className="text-lg font-bold">{cc.name}</span>
					<span className="text-sm font-semibold">
						({cc.contactCentreCode})
					</span>
				</div>
				<span className="text-sm font-semibold">{cc.productName}</span>
			</div>
		</>
	);

	if (loading) {
		return (
			<LoadingModal
				open={loading}
				message="Loading your contact centres, please hold on!"
			/>
		); // Pass the optional message
	}

	return (
		<EntityList<IContactCentreModel>
			data={data}
			selectMode={selectMode} // or false depending on your requirements
			onItemSelected={(cc) => handleSelectContactCentre(cc)}
			onEditClicked={(cc) => handleSelectContactCentre(cc)}
			renderChildren={renderContactCentreItem}
			filterText="Filter by contact center names"
			onAddNewClick={onAddNewClick}
			canAddNew={canCreateContactCenter}
			listLabel={"Choose Your Contact Centre"}
			enableEdit={true}
			enableDelete={false}
			enableView={false}
		/>
	);
};

export default ContactCentreList;
